import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import * as client from "../../../client";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import {
  ParsedTemplate,
  TemplateUseCase,
  useAddTemplate,
  useDeleteTemplate,
  useTemplates,
  useUpdateTemplate,
} from "./data";
import { FileUpload } from "primereact/fileupload";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { downloadTemplate } from "../../../api/routes";
import { downloadBlob } from "../../../util";
import { useState } from "react";
import { useUsers } from "../data";
import { Message } from "primereact/message";

const formatDateString = (dateString: string): string => {
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  return new Intl.DateTimeFormat("en-US", options).format(date);
};

const useCaseNames: { [useCase in TemplateUseCase]: string } = {
  proposal: "Proposal",
  rater: "Rater",
  binder: "Binder",
  terrorism_declaration: "Terrorism Declaration Page",
  property_declaration: "Property Declaration Page",
};

const useCaseAccepts: { [useCase in TemplateUseCase]: string } = {
  proposal: ".docx",
  rater: ".xlsx,.xlsm",
  binder: ".docx",
  terrorism_declaration: ".docx",
  property_declaration: ".docx",
};

export const TemplateDialog = ({
  useCase,
  visible,
  onHide,
}: {
  useCase: TemplateUseCase;
  visible: boolean;
  onHide: () => void;
}) => {
  const [error, setError] = useState("");
  const users = useUsers();
  // TemplateButton renders this only behind its template-management check.
  const templates = useTemplates(useCase, true);
  const updateTemplate = useUpdateTemplate();
  const addTemplate = useAddTemplate();
  const deleteTemplate = useDeleteTemplate();
  const [isDownloading, setIsDownloading] = useState<
    client.Template | undefined
  >(undefined);
  const pending =
    templates.isPending ||
    updateTemplate.isPending ||
    addTemplate.isPending ||
    deleteTemplate.isPending;

  return (
    <Dialog
      header={`${useCaseNames[useCase]} Template`}
      visible={visible}
      onHide={onHide}
      style={{
        minWidth: "600px",
        maxHeight: "80vh",
      }}
      contentStyle={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
      draggable={false}
    >
      <DataTable<ParsedTemplate[]>
        value={templates.data}
        loading={pending}
        style={{ maxHeight: "60vh" }}
        sortField="created_at_ts"
      >
        <Column
          columnKey="created_at_ts"
          header="Created"
          field="created_at_ts"
          body={(template: client.Template) =>
            formatDateString(template.created_at_ts)
          }
        />
        <Column
          columnKey="author_id"
          header="Author"
          field="author_id"
          body={(template: client.Template) => {
            const author = users.data?.all_company_users.filter(
              (u) => u.id === template.author_id
            )[0];
            return author ? (author.full_name ?? author.email) : "Unknown";
          }}
        />
        <Column
          columnKey="template_name"
          header="Name"
          field="template_name"
          body={(template: client.Template) => (
            <span style={{ display: "flex", alignItems: "center" }}>
              {template.template_name}{" "}
              <Button
                icon="pi pi-download"
                tooltip="download"
                tooltipOptions={{ showDelay: 500 }}
                loading={isDownloading?.template_id === template.template_id}
                size="small"
                link
                onClick={async () => {
                  try {
                    setIsDownloading(template);
                    downloadBlob(
                      template.template_name,
                      await downloadTemplate(template)
                    );
                  } finally {
                    setIsDownloading(undefined);
                  }
                }}
              />
            </span>
          )}
        />
        <Column
          columnKey="live"
          header="Live"
          field="live"
          body={(template: client.Template) => (
            <Checkbox
              disabled={!!template.live}
              checked={!!template.live}
              onChange={(e) =>
                updateTemplate.mutate({
                  template_id: template.template_id,
                  live: !!e.checked,
                })
              }
              tooltip={
                template.live
                  ? "This version is live."
                  : "Click to publish this version."
              }
              tooltipOptions={{ showOnDisabled: true, showDelay: 500 }}
            />
          )}
        />
        <Column
          columnKey="trash"
          header="Delete"
          body={(template: client.Template) => (
            <Button
              onClick={() =>
                confirmDialog({
                  header: `Delete ${template.template_name}?`,
                  message: "This cannot be undone.",
                  acceptLabel: "Delete",
                  acceptClassName: "p-button-danger",
                  rejectLabel: "Cancel",
                  accept: () =>
                    deleteTemplate.mutate({
                      template_id: template.template_id,
                    }),
                  closable: false,
                })
              }
              icon="pi pi-trash"
              severity="danger"
              text
              disabled={!!template.live}
              tooltip={
                template.live
                  ? "The live template cannot be deleted."
                  : undefined
              }
              tooltipOptions={{ showOnDisabled: true, showDelay: 500 }}
              size="small"
            />
          )}
        />
      </DataTable>
      <FileUpload
        accept={useCaseAccepts[useCase]}
        mode="basic"
        chooseLabel="Add version"
        auto
        customUpload
        uploadHandler={(e) => {
          const file = e.files[0];
          if (file) {
            if (templates.data?.some((t) => t.template_name === file.name)) {
              setError("Please use a unique filename for this new version.");
            } else {
              addTemplate.mutate({ template: file, useCase });
              setError("");
            }
          }
        }}
      />
      {error && <Message severity="error" text={error} />}
    </Dialog>
  );
};
